import type { CybersecurityScoreResponse } from "shared-core/types/cybersecurity-score";
import type { OrganizationOverview } from "shared-core/types/organization";
import type { MfaSettingsResponse } from "shared-core/types/settings";
import type {
  SkipReason,
  Task,
  TaskAutoFixStatusResponse,
  TaskCompletionMethod,
  TaskMilestoneEnum,
  TaskStatusResponse,
  TimelineMilestones,
} from "shared-core/types/task";
import type { EmployeeData } from "shared-core/types/user";
import type { TaskStatusSyncOptions } from "./device-task-automation-service";

export const MainStoreInjectKey = Symbol("main-store");

export interface MainStoreActions {
  fetchTasks(options?: TaskStatusSyncOptions): Promise<void>;

  fetchCyberScore(): Promise<void>;

  fetchMfaStatus(): Promise<void>;

  fetchUsers(options?: { includeDeactivated?: boolean }): Promise<void>;

  markAsComplete(
    taskId: number,
    userEmail: string,
    deviceInventoryId?: string | null,
    completionMethod?: TaskCompletionMethod,
  ): Promise<TaskMilestoneEnum[]>;

  markAsSkipped(
    taskId: number,
    userEmail: string,
    skipReason: SkipReason,
    skipReasonText?: string | null,
    skipDelayHours?: number | null,
    deviceInventoryId?: string | null,
  ): Promise<TaskMilestoneEnum[]>;

  markCompletionsSeen(taskIds: number[]): Promise<void>;

  fetchTaskAutoFixStatus(taskId: number): Promise<TaskAutoFixStatusResponse>;

  refreshTaskStatus(taskId: number): Promise<TaskStatusResponse>;

  automate(taskId: number, taskReference: string): Promise<TaskStatusResponse>;

  startRiskAnalysis(): Promise<void>;

  fetchOrganizationOverview(forceUpdate?: boolean): Promise<void>;
}

export interface EmployeeDataMainStore extends EmployeeData {
  fullName: string;
}

export interface MainStoreState {
  users: EmployeeDataMainStore[];
  tasks: Task[];
  organizationOverview: OrganizationOverview | null;
  cyberScoreStatus: null | CybersecurityScoreResponse;
  mfaStatus: null | MfaSettingsResponse;
  milestones: TimelineMilestones;
}

export interface MainStoreGetters {
  threatsIdentified: number;
}

/** Extended state provided by app main stores (includes user-filtered tasks) */
export interface AppMainStoreState extends MainStoreState {
  userTasks: Task[];
}
